import type { ApiResponseBody } from "@/lib/api/api-type-helper";

type CustomerProductListItemRes = ApiResponseBody<
  "/v2/marketplace/store/products",
  "get"
>["items"][number];
type ProductPriceBE = CustomerProductListItemRes["price"];
type PricingMethodBE = ProductPriceBE["pricingMethod"];
type CurrencyBE = ProductPriceBE["currency"];
type CycleUnitBE = NonNullable<ProductPriceBE["recurring"]>["cycleUnit"];
type ContractTypeBE = CustomerProductListItemRes["contractMethod"];

type CustomerProductDetailRes = ApiResponseBody<
  "/v2/marketplace/store/products/{storeProductVariantId}",
  "get"
>;
type ProductDescriptionTypeBE =
  CustomerProductDetailRes["descriptions"][number]["descriptionType"];

/**
 * マーケットプレイスで使用される通貨の定義
 */
export const Currency = {
  USD: "USD",
  JPY: "JPY",
  IDR: "IDR",
  THB: "THB",
  VND: "VND",
  CNY: "CNY",
} as const satisfies { [K in CurrencyBE]: K };
export type Currency = (typeof Currency)[keyof typeof Currency];

/**
 * 契約種別の定義
 */
export const ContractType = {
  RENTAL: "RENTAL",
  PURCHASE: "PURCHASE",
  SERVICE_CONTRACT: "SERVICE_CONTRACT",
} as const satisfies { [K in ContractTypeBE]: K };
export type ContractType = (typeof ContractType)[keyof typeof ContractType];

/**
 * 商品の価格設定方法
 */
export const PricingMethod = {
  ONESHOT: "ONESHOT",
  RECURRING: "RECURRING",
  QUOTATION: "QUOTATION",
} as const satisfies { [K in PricingMethodBE]: K };
export type PricingMethod = (typeof PricingMethod)[keyof typeof PricingMethod];

/**
 * 価格の支払いサイクル単位
 */
export const CycleUnit = {
  DAY: "DAY",
  WEEK: "WEEK",
  MONTH: "MONTH",
  YEAR: "YEAR",
} as const satisfies { [K in CycleUnitBE]: K };
export type CycleUnit = (typeof CycleUnit)[keyof typeof CycleUnit];

/**
 * 商品の価格情報
 */
export interface ProductPrice {
  pricingMethod: PricingMethod;
  currency: Currency;
  taxExcludedAmount: string;
  taxAmount: string;
  taxIncludedAmount: string;
  recurring?: {
    cycleUnit: CycleUnit;
    periodUnit: CycleUnit;
    cycleLength: number;
    periodLength: number;
  };
}

/**
 * 商品のバリエーション
 */
type ProductVariant = {
  productVariantId: string;
  name: string;
  price: ProductPrice;
};

/**
 * 商品のオプションバリエーション
 */
type OptionVariant = {
  optionVariantId: string;
  productVariantId: string;
  name: string;
  price: ProductPrice;
};

/**
 * 商品のオプション
 */
export interface ProductOption {
  optionId: string;
  productId: string;
  name: string;
  noVariantLabel: string;
  required: boolean;
  variants: OptionVariant[];
}

/**
 * 商品の画像情報
 */
export type ProductImage = {
  fullsizeUrl: string;
  thumbnailUrl?: string;
};

/**
 * 商品の説明タイプ
 */
export const DescriptionType = {
  MAIN_DESCRIPTION: "MAIN_DESCRIPTION",
  DESCRIPTION: "DESCRIPTION",
  LINK: "LINK",
} as const satisfies { [K in ProductDescriptionTypeBE]: K };
export type DescriptionType =
  (typeof DescriptionType)[keyof typeof DescriptionType];

/**
 * 商品の説明情報
 */
export type ProductDescription = {
  descriptionType: DescriptionType;
  label: string;
  description?: string;
  linkUrl?: string;
};

/**
 * サプライヤーのプロフィール情報
 */
type SupplierProfile = {
  name: string;
  avatarUrl?: string;
  representative?: string;
};

/**
 * 商品の詳細情報
 */
export interface ProductDetail {
  productVariantId: string;
  name: string;
  mainImage?: ProductImage;
  subImages: ProductImage[];
  mainDescription?: string;
  descriptions: ProductDescription[];
  price: ProductPrice;
  otherVariants: ProductVariant[];
  options: ProductOption[];
  supplierProfile: SupplierProfile;
}
